import { useState, useEffect, useMemo } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Menu, X, ExternalLink } from "lucide-react";
import {
  selectProviderIsAuthenticated,
  selectProviderLoginTime,
  providerLogout,
} from "@/store/providerAuthSlice";
import { cn } from "@/lib/utils";
import { PROVIDER_MENU, type ProviderMenuItem } from "./providerMenu";
import { ProviderFooter } from "./ProviderFooter";
import { useGetFrontNavMenusQuery } from "@/services/menuApi";

/** 프로그램 등록센터(=HMPG_SE_CD 'E') GNB 메뉴 코드 */
const PROVIDER_HMPG_SE_CD = "E";

export function ProviderMainLayout() {
  const isAuthenticated = useSelector(selectProviderIsAuthenticated);
  const loginTime = useSelector(selectProviderLoginTime);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /**
   * Provider 사이드바 메뉴를 DB에서 동적으로 로드.
   * API 실패/로딩 중에는 hardcoded fallback(PROVIDER_MENU)을 사용해 UI 깨짐 방지.
   */
  const { data: dynamicMenus } = useGetFrontNavMenusQuery({
    hmpg_se_cd: PROVIDER_HMPG_SE_CD,
  });
  const navMenu: ProviderMenuItem[] = useMemo(() => {
    if (!dynamicMenus || dynamicMenus.length === 0) return PROVIDER_MENU;
    /* tree의 1depth 자식들이 사이드바 항목. 루트(프로그램 등록센터)는 헤더 표시용 */
    const root = dynamicMenus[0];
    const items = (root?.children ?? []).filter(
      (m) => m.use_yn !== "N" && m.gnb_use_yn !== "N",
    );
    return items
      .sort((a, b) => (a.sort_sn ?? 0) - (b.sort_sn ?? 0))
      .map((m) => ({ title: m.menu_nm, href: m.lnkg_addr || "#" }));
  }, [dynamicMenus]);

  // Auto-logout after 30 minutes with 5-minute warning
  useEffect(() => {
    if (!isAuthenticated || !loginTime) return;
    const SESSION_TIMEOUT = 30 * 60 * 1000;
    const WARN_BEFORE = 5 * 60 * 1000;
    const elapsed = Date.now() - loginTime;
    const remaining = SESSION_TIMEOUT - elapsed;
    if (remaining <= 0) {
      alert("세션이 만료되었습니다. 다시 로그인해주세요.");
      dispatch(providerLogout());
      navigate("/provider/login");
      return;
    }
    const timers: ReturnType<typeof setTimeout>[] = [];
    const warnRemaining = remaining - WARN_BEFORE;
    if (warnRemaining > 0) {
      timers.push(setTimeout(() => {
        alert("로그인 세션이 5분 후 만료됩니다. 작업을 저장해주세요.");
      }, warnRemaining));
    }
    timers.push(setTimeout(() => {
      alert("세션이 만료되었습니다. 다시 로그인해주세요.");
      dispatch(providerLogout());
      navigate("/provider/login");
    }, remaining));
    return () => timers.forEach(clearTimeout);
  }, [isAuthenticated, loginTime, dispatch, navigate]);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const openMobileMenu = () => {
    setMobileMenuVisible(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setMobileMenuOpen(true);
      });
    });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      setMobileMenuVisible(false);
    }, 200);
  };

  useEffect(() => {
    if (mobileMenuOpen) closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuVisible]);

  if (!isAuthenticated) {
    return <Navigate to="/provider/login" replace />;
  }

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + "/");

  const handleLogout = () => {
    dispatch(providerLogout());
    navigate("/provider/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Mobile Header */}
      <header
        className={cn(
          "lg:hidden bg-white border-b border-[#E0E0E0] shrink-0 relative z-50",
          mobileMenuOpen && "max-sm:border-b-0",
        )}
      >
        <div className="px-[16px] py-[20px] flex items-center justify-between">
          <Link to="/provider">
            <img
              src="/images/logo.svg"
              alt="삼척 돌봄e음"
              className="w-[87px] h-[40px] object-contain"
            />
          </Link>
          <button
            onClick={mobileMenuOpen ? closeMobileMenu : openMobileMenu}
            className="p-1 text-[#333]"
            aria-label={mobileMenuOpen ? "메뉴 닫기" : "메뉴 열기"}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </header>

      {/* Mobile Drawer */}
      {mobileMenuVisible && (
        <div className="fixed inset-0 top-[80px] z-40 lg:hidden">
          <div
            className={cn(
              "absolute inset-0 transition-opacity duration-200",
              mobileMenuOpen ? "opacity-100 bg-black/40" : "opacity-0",
            )}
            onClick={closeMobileMenu}
          />
          <div
            className={cn(
              "absolute top-0 right-0 w-full sm:w-[85%] sm:max-w-[400px] h-full bg-white flex flex-col transition-transform duration-200 ease-in-out",
              mobileMenuOpen ? "translate-x-0" : "translate-x-full",
            )}
          >
            <div className="flex gap-3 px-5 py-5">
              <button
                onClick={() => {
                  handleLogout();
                  closeMobileMenu();
                }}
                className="flex-1 h-[48px] flex items-center justify-center rounded-[8px] border border-[#D0D0D0] text-[15px] font-semibold text-[#333]"
              >
                로그아웃
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto">
              {navMenu.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={closeMobileMenu}
                  className={cn(
                    "flex items-center justify-between px-5 py-5 border-b border-[#F0F0F0] text-[17px] font-semibold",
                    isActive(item.href) ? "text-[#F69000]" : "text-[#333]",
                  )}
                >
                  {item.title}
                </Link>
              ))}
            </nav>

            <div className="border-t border-[#E0E0E0]">
              <a
                href="/"
                className="flex items-center gap-1 px-5 py-4 text-[14px] text-[#666] border-b border-[#F0F0F0]"
              >
                삼척돌봄e음 <ExternalLink size={14} />
              </a>
              <a
                href="https://www.edupia-samcheok.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-5 py-4 text-[14px] text-[#666]"
              >
                EDUPIA삼척교육발전사업단 <ExternalLink size={14} />
              </a>
              <div className="flex items-center justify-center gap-6 py-5">
                <a
                  href="https://www.instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[14px] text-[#666]"
                >
                  <img
                    src="/images/icon-instagram.svg"
                    alt="Instagram"
                    className="w-6 h-6"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  Instagram
                </a>
                <a
                  href="https://www.youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[14px] text-[#666]"
                >
                  <img
                    src="/images/icon-youtube.svg"
                    alt="Youtube"
                    className="w-6 h-6"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  Youtube
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop: Sidebar + (Header + Content) */}
      <div className="flex-1 hidden lg:flex">
        {/* Sidebar */}
        <aside className="2xl:max-w-[431px] max-w-80 w-full flex-shrink-0 bg-white border-r border-[#E0E0E0] px-[39px] flex flex-col duration-300">
          <Link to="/provider" className="pt-[56px] pb-[35px] flex justify-end">
            <img
              src="/images/logo.svg"
              alt="삼척 돌봄e음"
              className="w-[128px] h-[60px] object-contain"
            />
          </Link>

          <nav className="flex flex-col gap-1 w-[232px] ml-auto mt-[24px]">
            {navMenu.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center gap-[20px] pl-[24px] pr-5 py-[21px] text-[16px] font-medium transition-all duration-200 rounded-full",
                  isActive(item.href)
                    ? "bg-[#F87400] text-white font-bold"
                    : "text-[#666] hover:text-[#333]",
                )}
              >
                <svg
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M10.3867 5.45898L15.4824 3.80371L16.7031 7.56055L11.4492 9.26562L14.6553 13.6787L11.459 16L8.35059 11.7217L5.24414 16L2.04785 13.6787L5.25195 9.2666L0 7.56055L1.2207 3.80371L6.4375 5.49902V0H10.3867V5.45898Z"
                    fill={isActive(item.href) ? "white" : "#F87400"}
                  />
                </svg>
                {item.title}
              </Link>
            ))}
          </nav>
        </aside>

        {/* Right: Header + Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="bg-white border-b border-[#D9D9D9] shrink-0">
            <div className="px-10 pt-[56px] pb-[35px] flex items-center">
              <Link to="/provider" className="flex flex-col">
                <span className="text-[17.42px] font-semibold text-[#666666] leading-[130%]">
                  프로그램 운영자를 위한
                </span>
                <span className="text-[27.88px] font-semibold text-[#333] leading-[130%]">
                  프로그램 등록센터
                </span>
              </Link>

              <div className="ml-auto flex items-center text-[14px] font-bold text-[#666666] leading-none">
                <button
                  onClick={handleLogout}
                  className="hover:text-[#333] transition-colors"
                >
                  로그아웃
                </button>
                <span className="mx-3 w-px h-[14px] bg-[#A1A1A1]" />
                <Link to="/" className="hover:text-[#333] transition-colors">
                  삼척돌봄e음 돌아가기
                </Link>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Mobile Content */}
      <main className="flex-1 lg:hidden">
        <Outlet />
      </main>

      <ProviderFooter />
    </div>
  );
}
